import React, { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Activity,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronRight,
  Download,
  Flame,
  History,
  MapPin,
  MessageSquareText,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Tag,
  Trophy,
  Upload,
  UserCheck,
  UserPlus,
  X,
} from "lucide-react";
import { api, useAuth } from "./auth";
import { useSearchParams } from "react-router-dom";

type Row = Record<string, any>;
type LeadResult = {
  data: Row;
  history: Row[];
  comments: Row[];
  activities: Row[];
  matches: Row[];
  opportunities: Row[];
  assignments: Row[];
  customerProfile: Row | null;
};
const businessOptions = [
  ["GROUP", "Group pool"],
  ["REAL_ESTATE", "RealCO Capital"],
  ["BUSINESS_CENTER", "Central Royal"],
  ["CAR_RENTAL", "Royal Rentacar"],
  ["CLEANING", "Real Clean"],
  ["MAINTENANCE", "I Maintain"],
  ["HOLIDAY_HOMES", "Royal Maison Holiday Homes"],
];
const categories = [
  ["PROPERTY_BUYER", "Property buyer"],
  ["PROPERTY_TENANT", "Property tenant"],
  ["PROPERTY_SELLER", "Property seller"],
  ["PROPERTY_LANDLORD", "Property landlord"],
  ["OFFICE_TENANT", "Office tenant"],
  ["CAR_RENTAL", "Car rental customer"],
  ["CHAUFFEUR", "Chauffeur customer"],
  ["CLEANING", "Cleaning customer"],
  ["MAINTENANCE", "Maintenance customer"],
  ["HOLIDAY_HOME_GUEST", "Holiday-home guest"],
  ["HOLIDAY_HOME_OWNER", "Holiday-home owner"],
  ["GROUP_OPPORTUNITY", "Group opportunity"],
];
const emirates = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];
const label = (value: unknown) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const money = (value: unknown, currency = "AED") =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const tomorrowQuarterHour = () => {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  value.setSeconds(0, 0);
  value.setMinutes(Math.ceil(value.getMinutes() / 15) * 15);
  return new Date(value.getTime() - value.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
};

export function LeadCommand() {
  const { csrfToken, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]),
    [claims, setClaims] = useState<Row[]>([]),
    [companies, setCompanies] = useState<Row[]>([]),
    [people, setPeople] = useState<Row[]>([]),
    [teams, setTeams] = useState<Row[]>([]),
    [summary, setSummary] = useState<Row>({}),
    [working, setWorking] = useState<Row[]>([]),
    [campaignOpen, setCampaignOpen] = useState(false),
    [selectedIds, setSelectedIds] = useState<string[]>([]);
  const initialBusiness = businessOptions.some(
    ([code]) => code === searchParams.get("business"),
  )
    ? String(searchParams.get("business"))
    : "ALL";
  const [query, setQuery] = useState(""),
    [business, setBusiness] = useState(initialBusiness),
    [stage, setStage] = useState("ALL"),
    [selected, setSelected] = useState<Row | null>(null),
    [detail, setDetail] = useState<LeadResult | null>(null),
    [createOpen, setCreateOpen] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  function chooseBusiness(value: string) {
    setBusiness(value);
    value === "ALL"
      ? setSearchParams({})
      : setSearchParams({ business: value });
  }
  const canClaim = ["AGENT", "SUPERADMIN"].includes(user?.role || "");
  const canAllocate = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
    "TEAM_LEAD",
  ].includes(user?.role || "");
  const canImport = canAllocate || Boolean(user?.permissions?.includes("LEADS_IMPORT"));
  const canDeactivate = canAllocate || Boolean(user?.permissions?.includes("LEADS_DEACTIVATE"));
  async function load() {
    try {
      const [
        leadResult,
        claimResult,
        companyResult,
        assigneeResult,
        summaryResult,
        workingResult,
      ] = await Promise.all([
        api<{ data: Row[] }>("/api/v1/enterprise/leads"),
        canAllocate
          ? api<{ data: Row[] }>("/api/v1/enterprise/lead-claims")
          : Promise.resolve({ data: [] }),
        api<{ data: Row[] }>("/api/v1/master/legal-entities"),
        canAllocate
          ? api<{ users: Row[]; teams: Row[] }>(
              "/api/v1/enterprise/task-assignees",
            )
          : Promise.resolve({ users: [], teams: [] }),
        api<{ data: Row }>("/api/v1/enterprise/pipeline-dashboard"),
        api<{ data: Row[] }>("/api/v1/enterprise/working-list"),
      ]);
      setRows(leadResult.data);
      setClaims(claimResult.data);
      setCompanies(companyResult.data);
      setPeople(
        assigneeResult.users.filter((x) =>
          ["AGENT", "TEAM_LEAD", "STAFF"].includes(x.role),
        ),
      );
      setTeams(assigneeResult.teams);
      setSummary(summaryResult.data);
      setWorking(workingResult.data);
      setError("");
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Unable to load the lead workspace",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  const filtered = useMemo(
    () =>
      rows.filter(
        (row) =>
          (business === "ALL" || row.business_module === business) &&
          (stage === "ALL" || row.stage === stage) &&
          Object.values(row).some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(query.toLowerCase()),
          ),
      ),
    [rows, business, stage, query],
  );
  async function openLead(row: Row) {
    setSelected(row);
    setDetail(null);
    setMessage("");
    try {
      setDetail(
        await api<LeadResult>(`/api/v1/enterprise/lead-profile/${row.id}`),
      );
    } catch (value) {
      setMessage(
        value instanceof Error
          ? value.message
          : "Claim approval is required to open this profile.",
      );
    }
  }
  async function refreshDetail() {
    if (selected) await openLead(selected);
    await load();
  }
  async function claim(row: Row) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/leads/${row.id}/claim`, csrfToken, {
        method: "POST",
        body: JSON.stringify({
          reason: "Available and ready to contact this customer",
        }),
      });
      setMessage(
        user?.role === "SUPERADMIN"
          ? "Lead added to your working list."
          : "Claim sent for approval or added to your list if auto-approved.",
      );
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Claim request failed",
      );
    } finally {
      setBusy(false);
    }
  }
  async function decide(id: string, decision: "APPROVED" | "REJECTED") {
    if (!csrfToken) return;
    await api(`/api/v1/enterprise/lead-claims/${id}/decision`, csrfToken, {
      method: "POST",
      body: JSON.stringify({
        decision,
        reason:
          decision === "APPROVED"
            ? "Approved for immediate contact"
            : "Returned to the allocation pool",
      }),
    });
    await load();
  }
  async function importFile(file: File) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      const response = await fetch("/api/v1/enterprise/leads/import", {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": csrfToken,
          "content-type":
            file.type ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        body: file,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.code);
      setMessage(
        `${result.created} leads imported; ${result.skipped} rows skipped.`,
      );
      await load();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }
  async function downloadTemplate() {
    try {
      const response = await fetch("/api/v1/enterprise/leads/import-template", {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Template download failed");
      const blob = await response.blob(),
        url = URL.createObjectURL(blob),
        link = document.createElement("a");
      link.href = url;
      link.download = "realco-lead-import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Template download failed",
      );
    }
  }
  async function deactivateLead(row: Row, reason: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/leads/${row.id}/deactivate`, csrfToken, { method: "POST", body: JSON.stringify({ reason }) });
      setSelected(null); setDetail(null); setMessage("Lead deactivated. Its full history has been retained."); await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "Lead could not be deactivated"); }
    finally { setBusy(false); }
  }
  return (
    <div className="content enterprise-page lead-workspace">
      <section className="page-heading animated-heading">
        <div>
          <span className="kicker">Group customer acquisition</span>
          <h1>Leads & Sales Pipeline</h1>
          <p>
            One group pool with separate business pipelines, protected contact
            details, approval-controlled claims and a complete customer history.
          </p>
        </div>
        <div className="heading-actions">
          {canImport && <button
            className="button secondary"
            onClick={() => void downloadTemplate()}
          >
            <Download />
            Download Excel template
          </button>}
          {canImport && (
            <label className="button secondary file-button">
              <Upload />
              Bulk upload
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) =>
                  event.target.files?.[0] &&
                  void importFile(event.target.files[0])
                }
              />
            </label>
          )}
          <button
            className="button primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus />
            New lead
          </button>
          {canAllocate && (
            <button
              className="button primary"
              onClick={() => setCampaignOpen(true)}
            >
              <Send />
              Campaign
            </button>
          )}
        </div>
      </section>
      {message && <div className="notice-banner">{message}</div>}
      {error && <div className="form-error">{error}</div>}
      <section className="metric-ribbon">
        <Metric
          icon={<Flame />}
          label="Leads in scope"
          value={summary.total ?? rows.length}
          detail="Group and company records"
        />
        <Metric
          icon={<UserCheck />}
          label="Available"
          value={
            summary.fresh ??
            rows.filter((x) => x.claim_status === "UNCLAIMED").length
          }
          detail="Contact details protected"
        />
        <Metric
          icon={<ShieldCheck />}
          label="Claimed"
          value={rows.filter((x) => x.claim_status === "CLAIMED").length}
          detail={`${claims.length} awaiting approval`}
        />
        <Metric
          icon={<Trophy />}
          label="My working list"
          value={working.length}
          detail="Active 24-hour assignments"
        />
      </section>
      <section className="pipeline-command-grid">
        <article className="panel stage-distribution">
          <header>
            <div>
              <span className="kicker">Pipeline health</span>
              <h2>Customers by stage</h2>
            </div>
            <BarChart3 />
          </header>
          {Object.entries(summary.stages || {}).map(([name, value]) => (
            <button
              type="button"
              key={name}
              className={stage === name ? "active" : ""}
              onClick={() => setStage(stage === name ? "ALL" : name)}
              aria-label={`Filter pipeline to ${label(name)}`}
            >
              <span>{label(name)}</span>
              <div>
                <i
                  style={{
                    width: `${Math.max(3, (Number(value) / Math.max(1, Number(summary.total))) * 100)}%`,
                  }}
                />
              </div>
              <strong>{String(value)}</strong>
            </button>
          ))}
          {stage !== "ALL" && (
            <button
              type="button"
              className="clear-drill"
              onClick={() => setStage("ALL")}
            >
              Clear drill-down · showing {label(stage)}
            </button>
          )}
        </article>
        <article className="panel working-list-mini">
          <header>
            <div>
              <span className="kicker">Personal working list</span>
              <h2>24-hour claim clock</h2>
            </div>
            <UserCheck />
          </header>
          {working.map((item) => (
            <button
              key={item.assignment_id}
              onClick={() => {
                const row = rows.find((x) => x.id === item.id);
                if (row) void openLead(row);
              }}
            >
              <div>
                <strong>{item.display_name}</strong>
                <small>
                  {item.reference} · {label(item.stage)}
                </small>
              </div>
              <span>
                {Math.max(
                  0,
                  Math.ceil(
                    (Date.parse(item.auto_return_at) - Date.now()) / 3600000,
                  ),
                )}
                h
              </span>
              <ChevronRight />
            </button>
          ))}
          {!working.length && (
            <div className="empty">No active lead assignments.</div>
          )}
        </article>
      </section>
      <div className="business-tabs" role="tablist">
        <button
          className={business === "ALL" ? "active" : ""}
          onClick={() => chooseBusiness("ALL")}
        >
          All group <b>{rows.length}</b>
        </button>
        {businessOptions.map(([code, name]) => (
          <button
            key={code}
            className={business === code ? "active" : ""}
            onClick={() => chooseBusiness(code)}
          >
            {name}
            <b>{rows.filter((x) => x.business_module === code).length}</b>
          </button>
        ))}
      </div>
      {claims.length > 0 && (
        <section className="panel claim-strip">
          <header>
            <div>
              <span className="kicker">Team-lead action</span>
              <h2>Claims awaiting a decision</h2>
            </div>
          </header>
          <div>
            {claims.map((claim) => (
              <article key={claim.id}>
                <div>
                  <strong>{claim.display_name}</strong>
                  <small>
                    {claim.reference} · {claim.requested_by_name} ·{" "}
                    {claim.team_name}
                  </small>
                </div>
                <button onClick={() => void decide(claim.id, "REJECTED")}>
                  Reject
                </button>
                <button
                  className="approve"
                  onClick={() => void decide(claim.id, "APPROVED")}
                >
                  Approve
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="panel table-panel lead-table">
        <header>
          <div>
            <span className="kicker">Live allocation pool</span>
            <h2>
              {business === "ALL"
                ? "Every visible lead"
                : businessOptions.find((x) => x[0] === business)?.[1]}
            </h2>
          </div>
          <div className="lead-filters">
            <label className="search-control">
              <Search />
              <input
                placeholder="Search name, area, category, requirement…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <select
              value={stage}
              onChange={(event) => setStage(event.target.value)}
            >
              <option value="ALL">All stages</option>
              {[
                "NEW",
                "ATTEMPTING",
                "CONTACTED",
                "QUALIFIED",
                "VIEWING",
                "PROPOSAL",
                "NEGOTIATION",
                "WON",
                "DEAL_CLOSED",
                "LOST",
              ].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {canAllocate && (
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select visible leads"
                      checked={
                        filtered.length > 0 &&
                        filtered.every((x) => selectedIds.includes(x.id))
                      }
                      onChange={(event) =>
                        setSelectedIds(
                          event.target.checked
                            ? Array.from(
                                new Set([
                                  ...selectedIds,
                                  ...filtered.map((x) => x.id),
                                ]),
                              )
                            : selectedIds.filter(
                                (id) => !filtered.some((x) => x.id === id),
                              ),
                        )
                      }
                    />
                  </th>
                )}
                <th>Customer</th>
                <th>Business & category</th>
                <th>Location</th>
                <th>Requirement</th>
                <th>Budget</th>
                <th>Assignment</th>
                <th>Next action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const myAssignment = row.active_assignments?.find(
                  (item: Row) => item.userId === user?.id,
                );
                return (
                  <tr
                    key={row.id}
                    onClick={() => void openLead(row)}
                    className={`clickable-row ${row.overdue ? "lead-overdue" : ""}`}
                  >
                    {canAllocate && (
                      <td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.display_name}`}
                          checked={selectedIds.includes(row.id)}
                          onChange={(event) =>
                            setSelectedIds((ids) =>
                              event.target.checked
                                ? [...ids, row.id]
                                : ids.filter((id) => id !== row.id),
                            )
                          }
                        />
                      </td>
                    )}
                    <td>
                      <strong>{row.display_name}</strong>
                      <small>
                        {row.reference} ·{" "}
                        {row.email || row.mobile || "Contact protected"}
                      </small>
                      {row.overdue && (
                        <span className="overdue-badge">Contact overdue</span>
                      )}
                      {["WON", "LOST", "DEAL_CLOSED"].includes(row.stage) && (
                        <span
                          className={`lead-result-badge ${String(row.stage).toLowerCase().replace("_", "-")}`}
                        >
                          {label(row.stage)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="status">
                        {businessOptions.find(
                          (x) => x[0] === row.business_module,
                        )?.[1] || label(row.business_module)}
                      </span>
                      <small>{label(row.lead_category)}</small>
                    </td>
                    <td>
                      {row.community ||
                        row.area ||
                        row.city ||
                        row.emirate ||
                        "UAE"}
                      <small>
                        {row.property_type
                          ? label(row.property_type)
                          : "Details pending"}
                      </small>
                    </td>
                    <td>
                      {row.transaction_type
                        ? label(row.transaction_type)
                        : "General enquiry"}
                      <small>
                        {row.stage} · {row.temperature}
                      </small>
                    </td>
                    <td>
                      {money(row.budget_max || row.budget_min, row.currency)}
                    </td>
                    <td>
                      {row.assigned_user_name || "Unassigned"}
                      <small>{row.assigned_team_name || "Open pool"}</small>
                    </td>
                    <td>
                      {myAssignment ? (
                        <span className="claimed-stamp">
                          <UserCheck />
                          Claimed
                          <small>
                            {new Date(myAssignment.assignedAt).toLocaleString(
                              "en-AE",
                            )}
                          </small>
                        </span>
                      ) : !["WON", "LOST", "DEAL_CLOSED", "ARCHIVED"].includes(
                          row.stage,
                        ) &&
                        (row.active_assignments?.length || 0) < 3 &&
                        canClaim ? (
                        <button
                          className="table-action"
                          disabled={
                            busy || row.claim_status === "CLAIM_PENDING"
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            void claim(row);
                          }}
                        >
                          {row.claim_status === "CLAIM_PENDING"
                            ? "Approval pending"
                            : "Claim lead"}
                        </button>
                      ) : (
                        <ChevronRight />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && (
            <div className="empty">No leads match these filters.</div>
          )}
        </div>
      </section>
      {createOpen && (
        <LeadCreate
          companies={companies}
          csrfToken={csrfToken}
          onClose={() => setCreateOpen(false)}
          onSaved={async () => {
            setCreateOpen(false);
            setMessage("Lead created in the correct pipeline.");
            await load();
          }}
        />
      )}
      {campaignOpen && (
        <CampaignCreate
          csrfToken={csrfToken}
          leadIds={selectedIds}
          business={business === "ALL" ? "GROUP" : business}
          onClose={() => setCampaignOpen(false)}
          onSaved={() => {
            setCampaignOpen(false);
            setMessage("Campaign saved with its selected recipients.");
          }}
        />
      )}
      {selected && (
        <LeadDrawer
          row={selected}
          result={detail}
          message={message}
          csrfToken={csrfToken}
          canAllocate={canAllocate}
          canClaim={canClaim}
          canDeactivate={canDeactivate}
          people={people}
          teams={teams}
          companies={companies}
          onClose={() => {
            setSelected(null);
            setDetail(null);
            setMessage("");
          }}
          onChanged={refreshDetail}
          onClaim={() => claim(selected)}
          onDeactivate={(reason) => deactivateLead(selected, reason)}
        />
      )}
    </div>
  );
}

function LeadCreate({
  companies,
  csrfToken,
  onClose,
  onSaved,
}: {
  companies: Row[];
  csrfToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [business, setBusiness] = useState("GROUP"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/enterprise/leads", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          displayName: d.displayName,
          email: d.email || null,
          mobile: d.mobile || null,
          legalEntityId: d.legalEntityId || null,
          businessModule: d.businessModule,
          leadCategory: d.leadCategory,
          source: d.source,
          emirate: d.emirate,
          city: d.city || null,
          area: d.area || null,
          community: d.community || null,
          propertyType: d.propertyType || null,
          transactionType: d.transactionType || null,
          bedrooms: d.bedrooms ? Number(d.bedrooms) : null,
          bathrooms: d.bathrooms ? Number(d.bathrooms) : null,
          sizeMinSqft: d.sizeMinSqft ? Number(d.sizeMinSqft) : null,
          sizeMaxSqft: d.sizeMaxSqft ? Number(d.sizeMaxSqft) : null,
          budgetMin: d.budgetMin ? Number(d.budgetMin) : null,
          budgetMax: d.budgetMax ? Number(d.budgetMax) : null,
          currency: d.currency,
          requirement: d.requirement || null,
          temperature: d.temperature,
        }),
      });
      onSaved();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Lead could not be created",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form className="modal enterprise-modal lead-modal" onSubmit={submit}>
        <header>
          <div>
            <span className="kicker">Structured customer intake</span>
            <h2>Add lead</h2>
            <p>
              Only a name and one contact method are required. Add qualification
              details now to improve matching and allocation.
            </p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {error && <div className="form-error">{error}</div>}
        <div className="form-grid">
          <label>
            Customer name
            <input name="displayName" autoFocus required minLength={2} />
          </label>
          <label>
            Lead category
            <select name="leadCategory" required>
              {categories.map(([code, name]) => (
                <option value={code} key={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Email
            <input name="email" type="email" />
          </label>
          <label>
            Mobile
            <input name="mobile" placeholder="+971" />
          </label>
          <label>
            Business pipeline
            <select
              name="businessModule"
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
            >
              {businessOptions.map(([code, name]) => (
                <option value={code} key={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Responsible company
            <select
              name="legalEntityId"
              disabled={business === "GROUP"}
              required={business !== "GROUP"}
            >
              <option value="">
                {business === "GROUP" ? "Group-owned lead" : "Choose company"}
              </option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.trade_name || company.legal_name_en}
                </option>
              ))}
            </select>
          </label>
          <label>
            Source
            <select name="source">
              {[
                "WEBSITE",
                "BAYUT",
                "PROPERTY_FINDER",
                "DUBIZZLE",
                "WHATSAPP",
                "META_ADS",
                "GOOGLE_ADS",
                "REFERRAL",
                "WALK_IN",
                "MANUAL",
              ].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label>
            Temperature
            <select name="temperature">
              <option>WARM</option>
              <option>HOT</option>
              <option>COLD</option>
            </select>
          </label>
          <label>
            Emirate
            <select name="emirate" defaultValue="Dubai">
              {emirates.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label>
            City
            <input name="city" defaultValue="Dubai" />
          </label>
          <label>
            Area
            <input name="area" />
          </label>
          <label>
            Community
            <input name="community" />
          </label>
          <label>
            Transaction
            <select name="transactionType">
              <option value="">Not specified</option>
              <option>SALE</option>
              <option>RENT</option>
              <option>SHORT_TERM</option>
              <option>SERVICE</option>
              <option>RENTAL</option>
            </select>
          </label>
          <label>
            Property / request type
            <input name="propertyType" />
          </label>
          <label>
            Bedrooms
            <input name="bedrooms" type="number" min="0" step="0.5" />
          </label>
          <label>
            Bathrooms
            <input name="bathrooms" type="number" min="0" step="0.5" />
          </label>
          <label>
            Minimum size (sq ft)
            <input name="sizeMinSqft" type="number" min="0" />
          </label>
          <label>
            Maximum size (sq ft)
            <input name="sizeMaxSqft" type="number" min="0" />
          </label>
          <label>
            Minimum budget
            <input name="budgetMin" type="number" min="0" />
          </label>
          <label>
            Maximum budget
            <input name="budgetMax" type="number" min="0" />
          </label>
          <label>
            Currency
            <select name="currency">
              {["AED", "USD", "GBP", "EUR", "PKR", "INR"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Requirement
            <textarea
              name="requirement"
              rows={4}
              placeholder="Customer needs, preferred timing, constraints and useful context"
            />
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? "Creating…" : "Create lead"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function LeadDrawer({
  row,
  result,
  message,
  csrfToken,
  canAllocate,
  canClaim,
  canDeactivate,
  people,
  teams,
  companies,
  onClose,
  onChanged,
  onClaim,
  onDeactivate,
}: {
  row: Row;
  result: LeadResult | null;
  message: string;
  csrfToken: string | null;
  canAllocate: boolean;
  canClaim: boolean;
  canDeactivate: boolean;
  people: Row[];
  teams: Row[];
  companies: Row[];
  onClose: () => void;
  onChanged: () => void;
  onClaim: () => void;
  onDeactivate: (reason: string) => void;
}) {
  const { user } = useAuth();
  const data = result?.data;
  const [comment, setComment] = useState(""),
    [activity, setActivity] = useState(""),
    [stage, setStage] = useState(""),
    [stageComment, setStageComment] = useState(""),
    [nextActionAt, setNextActionAt] = useState(tomorrowQuarterHour()),
    [person, setPerson] = useState(""),
    [team, setTeam] = useState(""),
    [businessCompany, setBusinessCompany] = useState(""),
    [businessModule, setBusinessModule] = useState("REAL_ESTATE"),
    [tab, setTab] = useState("overview"),
    [busy, setBusy] = useState(false),
    [actionError, setActionError] = useState("");
  async function action(path: string, body: Row) {
    if (!csrfToken) return;
    setBusy(true);
    setActionError("");
    try {
      await api(path, csrfToken, {
        method: "POST",
        body: JSON.stringify(body),
      });
      await onChanged();
    } catch (value) {
      setActionError(value instanceof Error ? value.message : "Action failed");
      throw value;
    } finally {
      setBusy(false);
    }
  }
  async function updateStage() {
    if (!csrfToken || !stage || stageComment.trim().length < 3) return;
    setBusy(true);
    try {
      await api(
        `/api/v1/enterprise/working-list/${row.id}/progress`,
        csrfToken,
        {
          method: "POST",
          body: JSON.stringify({
            targetStage: stage,
            comment: stageComment,
            outcome:
              stage === "WON"
                ? "Deal won"
                : stage === "LOST"
                  ? "Opportunity lost"
                  : "Pipeline progressed",
            nextActionAt: ["WON", "LOST", "DEAL_CLOSED"].includes(stage)
              ? null
              : new Date(nextActionAt).toISOString(),
            lostReason: stage === "LOST" ? stageComment : null,
          }),
        },
      );
      setStage("");
      setStageComment("");
      setNextActionAt(tomorrowQuarterHour());
      await onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function allocate() {
    if (!person || !team) return;
    await action(`/api/v1/enterprise/leads/${row.id}/allocate`, {
      userId: person,
      teamId: team,
    });
  }
  async function saveCustomerProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setActionError("");
    try {
      await api(
        `/api/v1/enterprise/leads/${row.id}/customer-profile`,
        csrfToken,
        {
          method: "PUT",
          body: JSON.stringify({
            dateOfBirth: d.dateOfBirth || null,
            nationality: d.nationality || null,
            preferredLanguage: d.preferredLanguage || null,
            preferredContactChannel: d.preferredContactChannel || null,
            contactTimePreference: d.contactTimePreference || null,
            occupation: d.occupation || null,
            employer: d.employer || null,
            incomeBand: d.incomeBand || null,
            wealthBand: d.wealthBand || null,
            maritalStatus: d.maritalStatus || null,
            interests: String(d.interests || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            favouriteFoods: String(d.favouriteFoods || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            lifestyleTags: String(d.lifestyleTags || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            familyProfile: [],
            importantDates: [],
            servicePreferences: {},
            financialProfile: {},
            communicationConsent: {
              email: d.emailConsent === "on",
              sms: d.smsConsent === "on",
              whatsapp: d.whatsappConsent === "on",
            },
          }),
        },
      );
      await onChanged();
    } catch (value) {
      setActionError(
        value instanceof Error
          ? value.message
          : "Customer profile could not be saved",
      );
    } finally {
      setBusy(false);
    }
  }
  const allStages = [
    "NEW",
    "ATTEMPTING",
    "CONTACTED",
    "QUALIFIED",
    "VIEWING",
    "PROPOSAL",
    "NEGOTIATION",
    "WON",
  ];
  const currentStage = String(data?.stage || row.stage || "NEW");
  const controlledStages =
    currentStage === "WON"
      ? ["DEAL_CLOSED"]
      : [
          ...allStages.slice(Math.max(0, allStages.indexOf(currentStage) + 1)),
          "LOST",
        ];
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="record-drawer enterprise-drawer lead-drawer">
        <header>
          <div>
            <span className="kicker">
              {row.reference} · {label(row.lead_category)}
            </span>
            <h2>{row.display_name}</h2>
            <p>
              {data
                ? `${data.email || "No email"} · ${data.mobile || "No mobile"}`
                : "Protected customer profile"}
            </p>
          </div>
          <div className="drawer-actions">{canDeactivate && !["ARCHIVED", "WON", "DEAL_CLOSED"].includes(String(data?.stage || row.stage)) && <button className="button danger" onClick={() => { const reason = window.prompt("Why is this lead being deactivated? Its history will be retained."); if (reason?.trim()) onDeactivate(reason.trim()); }}>Deactivate lead</button>}<button className="icon-button" onClick={onClose}><X /></button></div>
        </header>
        {actionError && <div className="form-error">{actionError}</div>}
        {!data ? (
          <section className="locked-profile">
            <ShieldCheck />
            <h3>Contact details protected</h3>
            <p>
              {message ||
                "Claim approval is required before the full profile becomes visible."}
            </p>
            {row.active_assignments?.some(
              (item: Row) => item.userId === user?.id,
            ) ? (
              <div className="claimed-stamp large">
                <UserCheck />
                <span>
                  Claimed to your working list
                  <small>
                    {new Date(
                      row.active_assignments.find(
                        (item: Row) => item.userId === user?.id,
                      )?.assignedAt,
                    ).toLocaleString("en-AE")}
                  </small>
                </span>
              </div>
            ) : canClaim &&
              !["WON", "LOST", "DEAL_CLOSED", "ARCHIVED"].includes(
                row.stage,
              ) ? (
              <button
                className="button primary"
                disabled={busy || row.claim_status === "CLAIM_PENDING"}
                onClick={onClaim}
              >
                {row.claim_status === "CLAIM_PENDING"
                  ? "Claim awaiting approval"
                  : "Request this lead"}
              </button>
            ) : null}
          </section>
        ) : (
          <>
            <nav className="drawer-tabs">
              {[
                ["overview", "Overview"],
                ["profile", "Customer profile"],
                ["allocations", "Allocations"],
                ["activity", "Activity"],
                ["comments", "Comments"],
                ["history", "History"],
              ].map(([id, name]) => (
                <button
                  key={id}
                  className={tab === id ? "active" : ""}
                  onClick={() => setTab(id)}
                >
                  {name}
                </button>
              ))}
            </nav>
            {tab === "overview" && (
              <>
                <section className="profile-facts">
                  <Fact
                    icon={<Tag />}
                    label="Category"
                    value={label(data.lead_category)}
                  />
                  <Fact
                    icon={<Building2 />}
                    label="Business"
                    value={
                      businessOptions.find(
                        (x) => x[0] === data.business_module,
                      )?.[1] || label(data.business_module)
                    }
                  />
                  <Fact
                    icon={<MapPin />}
                    label="Location"
                    value={[data.community, data.area, data.city, data.emirate]
                      .filter(Boolean)
                      .join(", ")}
                  />
                  <Fact
                    icon={<Trophy />}
                    label="Budget"
                    value={money(
                      data.budget_max || data.budget_min,
                      data.currency,
                    )}
                  />
                  <Fact icon={<Activity />} label="Stage" value={data.stage} />
                  <Fact
                    icon={<History />}
                    label="Last contact"
                    value={
                      data.last_activity_at
                        ? new Date(data.last_activity_at).toLocaleString(
                            "en-AE",
                          )
                        : "Not contacted"
                    }
                  />
                </section>
                <section>
                  <h3>Customer requirement</h3>
                  <p className="drawer-note">
                    {data.requirement ||
                      "Qualification details have not yet been recorded."}
                  </p>
                  <div className="tag-row">
                    {[
                      data.transaction_type,
                      data.property_type,
                      data.bedrooms && `${data.bedrooms} bedrooms`,
                      data.size_min_sqft && `${data.size_min_sqft}+ sq ft`,
                      data.temperature,
                    ]
                      .filter(Boolean)
                      .map(String)
                      .map((x) => (
                        <span key={x}>{label(x)}</span>
                      ))}
                  </div>
                </section>
                {!["LOST", "DEAL_CLOSED", "ARCHIVED"].includes(
                  currentStage,
                ) && (
                  <section className="lead-quick-actions">
                    <h3>Advance the pipeline</h3>
                    <div>
                      <select
                        value={stage}
                        onChange={(event) => setStage(event.target.value)}
                      >
                        <option value="">Change stage…</option>
                        {controlledStages.map((value) => (
                          <option key={value}>{value}</option>
                        ))}
                      </select>
                      <input
                        type="datetime-local"
                        step={900}
                        value={nextActionAt}
                        disabled={["WON", "LOST", "DEAL_CLOSED"].includes(
                          stage,
                        )}
                        onChange={(event) =>
                          setNextActionAt(event.target.value)
                        }
                        aria-label="Next update date and time"
                      />
                      <textarea
                        value={stageComment}
                        onChange={(event) =>
                          setStageComment(event.target.value)
                        }
                        placeholder="Required comment for this phase update"
                        rows={2}
                      />
                      <button
                        disabled={
                          !stage || stageComment.trim().length < 3 || busy
                        }
                        onClick={() => void updateStage()}
                      >
                        {stage === "DEAL_CLOSED"
                          ? "Close deal & enable commission"
                          : "Save progression"}
                      </button>
                    </div>
                  </section>
                )}
                {canAllocate && (
                  <section className="allocation-box business-allocation">
                    <h3>Business ownership & opportunities</h3>
                    <p className="drawer-note">
                      Allocate the primary business or tag this lead to several
                      group companies. Tagged businesses receive the lead in
                      their own pipeline.
                    </p>
                    <div>
                      <select
                        value={businessModule}
                        onChange={(e) => setBusinessModule(e.target.value)}
                      >
                        {businessOptions
                          .filter(([code]) => code !== "GROUP")
                          .map(([code, name]) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                      </select>
                      <select
                        value={businessCompany}
                        onChange={(e) => setBusinessCompany(e.target.value)}
                      >
                        <option value="">Choose company</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.trade_name || company.legal_name_en}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={!businessCompany || busy}
                        onClick={() =>
                          void action(
                            `/api/v1/enterprise/leads/${row.id}/tag-business`,
                            {
                              legalEntityId: businessCompany,
                              businessModule,
                              notes: "Shared opportunity",
                            },
                          )
                        }
                      >
                        <Tag />
                        Tag business
                      </button>
                      <button
                        disabled={!businessCompany || busy}
                        onClick={() =>
                          void action(
                            `/api/v1/enterprise/leads/${row.id}/assign-business`,
                            { legalEntityId: businessCompany, businessModule },
                          )
                        }
                      >
                        <Building2 />
                        Make primary
                      </button>
                    </div>
                    <div className="tag-row business-tags">
                      {(result.opportunities || []).map((opportunity) => (
                        <span key={opportunity.id}>
                          {opportunity.trade_name} ·{" "}
                          {label(opportunity.target_business_module)}
                        </span>
                      ))}
                      {!(result.opportunities || []).length && (
                        <small>No additional businesses tagged.</small>
                      )}
                    </div>
                    <h3>Allocate to an agent</h3>
                    <div>
                      <select
                        value={team}
                        onChange={(e) => setTeam(e.target.value)}
                      >
                        <option value="">Choose team</option>
                        {teams.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={person}
                        onChange={(e) => setPerson(e.target.value)}
                      >
                        <option value="">Choose person</option>
                        {people.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.display_name} · {label(x.role)}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={!person || !team || busy}
                        onClick={() => void allocate()}
                      >
                        <UserPlus />
                        Allocate
                      </button>
                    </div>
                  </section>
                )}
              </>
            )}
            {tab === "profile" && (
              <section>
                <h3>Sales intelligence profile</h3>
                <p className="drawer-note">
                  Optional information helps the team personalise
                  recommendations. Every change is retained in the lead history.
                </p>
                <form onSubmit={saveCustomerProfile}>
                  <div className="form-grid">
                    <label>
                      Date of birth
                      <input
                        name="dateOfBirth"
                        type="date"
                        defaultValue={
                          result.customerProfile?.date_of_birth?.slice(0, 10) ||
                          ""
                        }
                      />
                    </label>
                    <label>
                      Nationality
                      <input
                        name="nationality"
                        maxLength={2}
                        defaultValue={result.customerProfile?.nationality || ""}
                        placeholder="AE"
                      />
                    </label>
                    <label>
                      Preferred language
                      <select
                        name="preferredLanguage"
                        defaultValue={
                          result.customerProfile?.preferred_language || ""
                        }
                      >
                        <option value="">Not recorded</option>
                        <option>English</option>
                        <option>Arabic</option>
                        <option>Hindi</option>
                        <option>Urdu</option>
                        <option>Russian</option>
                        <option>French</option>
                        <option>Mandarin</option>
                      </select>
                    </label>
                    <label>
                      Preferred contact
                      <select
                        name="preferredContactChannel"
                        defaultValue={
                          result.customerProfile?.preferred_contact_channel ||
                          ""
                        }
                      >
                        <option value="">Not recorded</option>
                        <option>PHONE</option>
                        <option>EMAIL</option>
                        <option>SMS</option>
                        <option>WHATSAPP</option>
                      </select>
                    </label>
                    <label>
                      Best contact time
                      <select
                        name="contactTimePreference"
                        defaultValue={
                          result.customerProfile?.contact_time_preference || ""
                        }
                      >
                        <option value="">Any time</option>
                        <option>Morning</option>
                        <option>Afternoon</option>
                        <option>Evening</option>
                        <option>Weekend</option>
                      </select>
                    </label>
                    <label>
                      Marital status
                      <select
                        name="maritalStatus"
                        defaultValue={
                          result.customerProfile?.marital_status || ""
                        }
                      >
                        <option value="">Not recorded</option>
                        <option>Single</option>
                        <option>Married</option>
                        <option>Family</option>
                        <option>Prefer not to say</option>
                      </select>
                    </label>
                    <label>
                      Occupation
                      <input
                        name="occupation"
                        defaultValue={result.customerProfile?.occupation || ""}
                      />
                    </label>
                    <label>
                      Employer
                      <input
                        name="employer"
                        defaultValue={result.customerProfile?.employer || ""}
                      />
                    </label>
                    <label>
                      Income band
                      <select
                        name="incomeBand"
                        defaultValue={result.customerProfile?.income_band || ""}
                      >
                        <option value="">Not recorded</option>
                        <option>Under AED 250k</option>
                        <option>AED 250k–500k</option>
                        <option>AED 500k–1m</option>
                        <option>AED 1m–5m</option>
                        <option>AED 5m+</option>
                      </select>
                    </label>
                    <label>
                      Wealth band
                      <select
                        name="wealthBand"
                        defaultValue={result.customerProfile?.wealth_band || ""}
                      >
                        <option value="">Not recorded</option>
                        <option>Mass affluent</option>
                        <option>High net worth</option>
                        <option>Ultra-high net worth</option>
                        <option>Corporate</option>
                      </select>
                    </label>
                    <label className="span-2">
                      Interests
                      <input
                        name="interests"
                        defaultValue={(
                          result.customerProfile?.interests || []
                        ).join(", ")}
                        placeholder="Golf, schools, waterfront, investment"
                      />
                    </label>
                    <label>
                      Favourite foods
                      <input
                        name="favouriteFoods"
                        defaultValue={(
                          result.customerProfile?.favourite_foods || []
                        ).join(", ")}
                      />
                    </label>
                    <label>
                      Lifestyle tags
                      <input
                        name="lifestyleTags"
                        defaultValue={(
                          result.customerProfile?.lifestyle_tags || []
                        ).join(", ")}
                      />
                    </label>
                    <label className="check">
                      <input
                        name="emailConsent"
                        type="checkbox"
                        defaultChecked={Boolean(
                          result.customerProfile?.communication_consent?.email,
                        )}
                      />
                      Email consent
                    </label>
                    <label className="check">
                      <input
                        name="smsConsent"
                        type="checkbox"
                        defaultChecked={Boolean(
                          result.customerProfile?.communication_consent?.sms,
                        )}
                      />
                      SMS consent
                    </label>
                    <label className="check">
                      <input
                        name="whatsappConsent"
                        type="checkbox"
                        defaultChecked={Boolean(
                          result.customerProfile?.communication_consent
                            ?.whatsapp,
                        )}
                      />
                      WhatsApp consent
                    </label>
                  </div>
                  <footer>
                    <button className="button primary" disabled={busy}>
                      Save intelligence profile
                    </button>
                  </footer>
                </form>
              </section>
            )}
            {tab === "allocations" && (
              <section>
                <h3>Allocation and release history</h3>
                <p className="drawer-note">
                  Up to three active collaborators can work a lead. Unworked
                  claims return automatically after 24 hours.
                </p>
                <div className="timeline">
                  {(result.assignments || []).map((item) => (
                    <article key={item.id}>
                      <i />
                      <div>
                        <strong>
                          {item.user_name} · {label(item.status)}
                        </strong>
                        <p>
                          {label(item.assignment_type)} ·{" "}
                          {item.team_name || "No team"} · assigned by{" "}
                          {item.assigned_by_name}
                        </p>
                        <small>
                          {new Date(item.assigned_at).toLocaleString("en-AE")}
                          {item.released_at
                            ? ` · released ${new Date(item.released_at).toLocaleString("en-AE")}`
                            : ""}
                        </small>
                      </div>
                    </article>
                  ))}
                  {!(result.assignments || []).length && (
                    <div className="empty">No allocation history.</div>
                  )}
                </div>
              </section>
            )}
            {tab === "activity" && (
              <section>
                <h3>Customer timeline</h3>
                <div className="comment-box">
                  <textarea
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                    placeholder="Call outcome and next action…"
                  />
                  <button
                    disabled={!activity.trim() || busy}
                    onClick={() =>
                      void action(
                        `/api/v1/enterprise/leads/${row.id}/activities`,
                        {
                          activityType: "CALL",
                          subject: "Customer call",
                          body: activity,
                          outcome: "FOLLOW_UP_REQUIRED",
                        },
                      ).then(() => setActivity(""))
                    }
                  >
                    <Activity />
                    Log call
                  </button>
                </div>
                <div className="timeline">
                  {result.activities.map((item) => (
                    <article key={item.id}>
                      <i />
                      <div>
                        <strong>{label(item.activity_type)}</strong>
                        <p>{item.body || item.subject || item.outcome}</p>
                        <small>
                          {item.created_by_name} ·{" "}
                          {new Date(item.occurred_at).toLocaleString("en-AE")}
                        </small>
                      </div>
                    </article>
                  ))}
                  {!result.activities.length && (
                    <div className="empty">
                      No customer activity has been logged.
                    </div>
                  )}
                </div>
              </section>
            )}
            {tab === "comments" && (
              <section>
                <h3>Internal comments</h3>
                <div className="comment-box">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a useful customer note…"
                  />
                  <button
                    disabled={!comment.trim() || busy}
                    onClick={() =>
                      void action(
                        `/api/v1/enterprise/leads/${row.id}/comments`,
                        { body: comment },
                      ).then(() => setComment(""))
                    }
                  >
                    <MessageSquareText />
                    Add
                  </button>
                </div>
                {result.comments.map((item) => (
                  <article className="comment" key={item.id}>
                    <strong>{item.created_by_name}</strong>
                    <p>{item.body}</p>
                    <small>
                      {new Date(item.created_at).toLocaleString("en-AE")}
                    </small>
                  </article>
                ))}
                {!result.comments.length && (
                  <div className="empty">No internal comments yet.</div>
                )}
              </section>
            )}
            {tab === "history" && (
              <section>
                <h3>Retained change history</h3>
                <div className="timeline">
                  {result.history.map((item) => (
                    <article key={item.id}>
                      <i />
                      <div>
                        <strong>{label(item.action)}</strong>
                        <p>{item.changed_by_name}</p>
                        <small>
                          {new Date(item.changed_at).toLocaleString("en-AE")}
                        </small>
                      </div>
                    </article>
                  ))}
                  {!result.history.length && (
                    <div className="empty">No previous changes.</div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

function CampaignCreate({
  csrfToken,
  leadIds,
  business,
  onClose,
  onSaved,
}: {
  csrfToken: string | null;
  leadIds: string[];
  business: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/enterprise/campaigns", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          name: d.name,
          businessModule: business,
          channel: d.channel,
          subject: d.subject || undefined,
          content: d.content,
          leadIds,
          criteria: { source: "PIPELINE_SELECTION" },
          scheduledAt: d.scheduledAt
            ? new Date(String(d.scheduledAt)).toISOString()
            : null,
        }),
      });
      onSaved();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Campaign could not be saved",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.currentTarget === event.target && onClose()}
    >
      <form className="modal enterprise-modal" onSubmit={submit}>
        <header>
          <div>
            <span className="kicker">Customer engagement</span>
            <h2>Create campaign</h2>
            <p>
              {leadIds.length
                ? `${leadIds.length} selected customers`
                : "Select customers in the pipeline table before scheduling delivery."}
            </p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {error && <div className="form-error">{error}</div>}
        <div className="form-grid">
          <label>
            Campaign name
            <input name="name" required />
          </label>
          <label>
            Channel
            <select name="channel">
              <option>EMAIL</option>
              <option>SMS</option>
              <option>WHATSAPP</option>
            </select>
          </label>
          <label className="span-2">
            Email subject
            <input name="subject" />
          </label>
          <label className="span-2">
            Message
            <textarea name="content" required rows={6} />
          </label>
          <label>
            Schedule
            <input name="scheduledAt" type="datetime-local" />
          </label>
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy || !leadIds.length}>
            {busy ? "Saving…" : "Save campaign"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Metric({
  icon,
  label: heading,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  detail: string;
}) {
  return (
    <article>
      {icon}
      <div>
        <span>{heading}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}
function Fact({
  icon,
  label: heading,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      {icon}
      <span>{heading}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}
